import React, { ReactNode, useEffect } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import styled, { useTheme } from 'styled-components';

import { NdvrEmploymentStatus } from '@ndvr/auth-api';

import { MultiFilter, MultiFilterSection } from '@/components/MultiFilter';
import { dateRangeOptions, states, titleCase, TODAY_DATE_VALUE } from 'utils';
import { useKeywords } from 'views/admin/segmentation/keyword-queries';
import {
    FilterActions,
    FilterFormLayout,
    FormMultiSelect,
    FormRadioButtons,
    SearchInput,
    Title,
    VerticalLayout,
} from 'widgets';
import { DropdownLayout } from 'widgets/Dropdown';
import { DateRangeInput } from 'widgets/form/input/FormDateInput';
import { HorizontalLayout } from 'widgets/Layout';
import { Text } from 'widgets/Text';

import { PersonFilterLabels } from './PersonFilterLabels';
import { PersonFilterKeys, PersonFilters, usePersonFiltersContext } from './PersonFiltersContext';

type PersonFilterType = 'status' | 'login' | 'states' | 'keywords';

export const HouseholdFilterForm = ({
    centerContent,
    count,
    rightActions,
}: {
    centerContent?: ReactNode;
    count?: number;
    rightActions?: ReactNode;
}) => {
    const formMethods = useForm<PersonFilters>();
    const { reset: resetFormState } = formMethods;
    const { filters, isFilterActive, resetFilters } = usePersonFiltersContext();

    /**
     * This effect is for keeping PersonFiltersContext in sync with the FormState.
     * This implementation is cleaner than using controlled forms.
     */
    useEffect(() => {
        if (filters) {
            resetFormState(filters);
        }
    }, [filters, resetFormState]);

    return (
        <FormProvider {...formMethods}>
            <FilterFormLayout
                centerContent={centerContent}
                count={count}
                countLabel="persons"
                isFilterActive={isFilterActive}
                onResetFilters={() => resetFilters()}
                filterButtons={<PersonMultiFilter />}
                filterLabels={<PersonFilterLabels />}
                rightActions={rightActions}
            />
        </FormProvider>
    );
};

const statusOptions = Object.values(NdvrEmploymentStatus).map((ndvrEmploymentStatus) => ({
    value: ndvrEmploymentStatus,
    label: titleCase(ndvrEmploymentStatus),
}));

const PersonMultiFilter = () => {
    const { ndvrEmploymentStatusCount, statesFilterCount, lastLoginAtCount, keywordsFilterCount, resetFilters } =
        usePersonFiltersContext();

    const sections: MultiFilterSection<PersonFilterType>[] = [
        {
            id: 'status',
            label: 'Status',
            count: ndvrEmploymentStatusCount,
            onClear: () => resetFilters([PersonFilterKeys.ndvrEmploymentStatus]),
            content: <StatusFilterContent />,
        },
        {
            id: 'login',
            label: 'Logins',
            count: lastLoginAtCount,
            onClear: () => resetFilters([PersonFilterKeys.lastLoginAtStart, PersonFilterKeys.lastLoginAtEnd]),
            content: <LoginFilterContent />,
        },
        {
            id: 'states',
            label: 'State',
            count: statesFilterCount,
            onClear: () => resetFilters([PersonFilterKeys.states]),
            content: <StatesFilterContent />,
        },
        {
            id: 'keywords',
            label: 'Segmentation',
            count: keywordsFilterCount,
            onClear: () => resetFilters([PersonFilterKeys.keywordGUIDs]),
            content: <KeywordsFilterContent />,
        },
    ];

    return (
        <MultiFilter
            sections={sections}
            defaultSectionId="status"
            dropdownClassName="h-96 w-[440px] p-0"
            contentClassName="min-h-0 flex-1 overflow-y-auto p-4 pt-3"
        />
    );
};

const StatusFilterContent = () => {
    const { getValues } = useFormContext();
    const { resetFilters, patchFilters } = usePersonFiltersContext();

    return (
        <FilterSectionLayout $gap={16}>
            <Title content>People Status</Title>
            <FormMultiSelect name={PersonFilterKeys.ndvrEmploymentStatus} options={statusOptions} />
            <FilterActions
                onApply={() =>
                    patchFilters({
                        [PersonFilterKeys.ndvrEmploymentStatus]: getValues(PersonFilterKeys.ndvrEmploymentStatus),
                    })
                }
                onReset={() => resetFilters([PersonFilterKeys.ndvrEmploymentStatus])}
            />
        </FilterSectionLayout>
    );
};

const StatesFilterContent = () => {
    const { getValues } = useFormContext();
    const { resetFilters, patchFilters } = usePersonFiltersContext();
    const [searchTerm, setSearchTerm] = React.useState('');

    const stateOptions = React.useMemo(
        () =>
            states.map((state) => ({
                value: state.abbreviation,
                label: `${state.abbreviation} ${state.name}`,
            })),
        [],
    );

    return (
        <DropdownLayout
            top={
                <VerticalLayout $gap={16}>
                    <Title content>US States & Territories</Title>
                    <SearchInput
                        placeholder="Search States"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                    />
                </VerticalLayout>
            }
            bottom={
                <FilterActions
                    onApply={() =>
                        patchFilters({
                            [PersonFilterKeys.states]: getValues(PersonFilterKeys.states),
                        })
                    }
                    onReset={() => resetFilters([PersonFilterKeys.states])}
                />
            }
        >
            <FormMultiSelect name={PersonFilterKeys.states} options={stateOptions} searchTerm={searchTerm} />
        </DropdownLayout>
    );
};

const KeywordsFilterContent = () => {
    const { getValues } = useFormContext();
    const { resetFilters, patchFilters } = usePersonFiltersContext();
    const { data: allKeywords = [] } = useKeywords();

    const keywordOptions = allKeywords.map((keyword) => ({
        value: keyword.keywordGUID,
        label: keyword.label,
    }));

    return (
        <FilterSectionLayout $gap={16}>
            <Title content>Segmentation Keywords</Title>
            <FormMultiSelect name={PersonFilterKeys.keywordGUIDs} options={keywordOptions} />
            <FilterActions
                onApply={() =>
                    patchFilters({
                        [PersonFilterKeys.keywordGUIDs]: getValues(PersonFilterKeys.keywordGUIDs),
                    })
                }
                onReset={() => resetFilters([PersonFilterKeys.keywordGUIDs])}
            />
        </FilterSectionLayout>
    );
};

const LoginFilterContent = () => {
    const { getValues, setValue } = useFormContext();
    const { resetFilters, patchFilters } = usePersonFiltersContext();
    const theme = useTheme();

    return (
        <FilterSectionLayout $gap={16}>
            <Title content>Login Activity</Title>
            <DateRangeInput
                title="Custom Date Range"
                startName={PersonFilterKeys.lastLoginAtStart}
                endName={PersonFilterKeys.lastLoginAtEnd}
                $iconColor={theme.colors.cocoa500}
                $outline
            />
            <HorizontalLayout $rowCenter $gap={8}>
                <Separator />
                <Text $themeColor="subtle">OR</Text>
                <Separator />
            </HorizontalLayout>
            <FormRadioButtons
                title=""
                name={PersonFilterKeys.lastLoginAtStart}
                variant="outline"
                values={dateRangeOptions}
                onChange={() => setValue(PersonFilterKeys.lastLoginAtEnd, TODAY_DATE_VALUE)}
            />
            <FilterActions
                onApply={() => {
                    patchFilters({
                        [PersonFilterKeys.lastLoginAtStart]: getValues(PersonFilterKeys.lastLoginAtStart),
                        [PersonFilterKeys.lastLoginAtEnd]: getValues(PersonFilterKeys.lastLoginAtEnd),
                    });
                }}
                onReset={() => resetFilters([PersonFilterKeys.lastLoginAtStart, PersonFilterKeys.lastLoginAtEnd])}
            />
        </FilterSectionLayout>
    );
};

const FilterSectionLayout = styled(VerticalLayout)`
    width: 100%;
`;

const Separator = styled.div`
    width: 100%;
    background-color: ${({ theme }) => theme.border.light};
    height: 1px;
`;
